import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Button, Modal, Form, ProgressBar, Tab, Tabs, Badge } from 'react-bootstrap';
import { FaUpload, FaFolderPlus, FaFolder, FaPause, FaPlay, FaTrash } from 'react-icons/fa';
import ResumableUpload from '../components/ResumableUpload';
import axios from 'axios';
import FileBreadcrumb from '../components/FileBreadcrumb';
import FileList from '../components/FileList';
import ShareModal from '../components/ShareModal';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://wfshare.wishfin.com/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const FileBrowser = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [currentPath, setCurrentPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  
  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderUploadModal, setShowFolderUploadModal] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [useMultipart, setUseMultipart] = useState(false);
  const [uploadStep, setUploadStep] = useState('select'); // 'select', 'standard', 'multipart'
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const [activeUploads, setActiveUploads] = useState([]);
  const [showUploadsManager, setShowUploadsManager] = useState(false);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadTimeRemaining, setUploadTimeRemaining] = useState(null);
  const uploadStartTimeRef = useRef(null);

  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds === null || seconds === undefined) return 'Calculating...';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s remaining`;
    if (mins > 0) return `${mins}m ${secs}s remaining`;
    return `${secs}s remaining`;
  };

  const trackUploadProgress = (progressEvent) => {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setUploadProgress(percentCompleted);
    setUploadedBytes(progressEvent.loaded);
    setTotalBytes(progressEvent.total);

    const elapsedSeconds = (Date.now() - uploadStartTimeRef.current) / 1000;
    if (elapsedSeconds > 0.2) {
      const speed = progressEvent.loaded / elapsedSeconds;
      setUploadSpeed(speed);
      const remainingBytes = progressEvent.total - progressEvent.loaded;
      setUploadTimeRemaining(speed > 0 ? Math.round(remainingBytes / speed) : null);
    }
  };
  
  // Extract path from URL
  useEffect(() => {
    let path = '';
    if (location.pathname.startsWith('/folder/')) {
      path = location.pathname.slice(8); // Remove '/folder/' prefix
    }
    setCurrentPath(path);
  }, [location.pathname]);
  
  // Fetch files and folders
  useEffect(() => {
    fetchFiles(currentPath);
  }, [currentPath]);
  
  const fetchFiles = async (path) => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(
        `${API_URL}/list`,
        {
          params: { 
            prefix: path,
            user_id: user?.id // Pass user ID for permission check
          },
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setFolders(response.data.folders || []);
      setFiles(response.data.files || []);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load files';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleFolderClick = (folderPath) => {
    navigate(`/folder/${folderPath}`);
  };
  
  const handleDownloadClick = async (filePath) => {
    try {
      // Get download URL from API, passing user ID for tracking
      const response = await axios.get(
        `${API_URL}/download/${filePath}`,
        {
          headers: {
            'x-api-key': API_KEY
          },
          params: {
            user_id: user?.id // Pass user ID for download tracking
          }
        }
      );
      
      // Navigate directly to the download URL - will force download due to ResponseContentDisposition
      // The browser will automatically trigger download instead of opening the file
      window.location.href = response.data.download_url;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to get download URL';
      setError(errorMessage);
    }
  };
  
  const handleShareClick = (filePath) => {
    setSelectedFile(filePath);
    setShowShareModal(true);
  };
  
  const handleDeleteClick = (filePath) => {
    setFileToDelete(filePath);
    setShowDeleteModal(true);
  };
  
  const confirmDeleteFile = async () => {
    try {
      setError('');
      
      await axios.delete(
        `${API_URL}/admin/delete-file/${fileToDelete}`,
        {
          headers: {
            'x-api-key': API_KEY
          },
          params: {
            user_id: user?.id
          }
        }
      );
      
      // Close modal and refresh file list
      setShowDeleteModal(false);
      setFileToDelete(null);
      fetchFiles(currentPath);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete file';
      setError(errorMessage);
      setShowDeleteModal(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const file = selectedUploadFile || fileInputRef.current.files[0];
    if (!file) {
      setUploadError('Please select a file');
      return;
    }

    try {
      // Clear all previous messages before starting upload
      setUploadError('');
      setUploadSuccess('');
      setUploadProgress(0);
      setUploadedBytes(0);
      setTotalBytes(file.size);
      setUploadSpeed(0);
      setUploadTimeRemaining(null);
      uploadStartTimeRef.current = Date.now();
      setUploading(true);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', currentPath);

      // Upload the file with progress tracking
      const response = await axios.post(
        `${API_URL}/upload`,
        formData,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          },
          params: {
            user_id: user?.id
          },
          onUploadProgress: trackUploadProgress
        }
      );
      
      setUploadSuccess('File uploaded successfully');
      // Reset the file input
      fileInputRef.current.value = '';
      setSelectedUploadFile(null);
      // Refresh the file list
      fetchFiles(currentPath);
      // Close the modal and refresh page after a delay
      setTimeout(() => {
        setShowUploadModal(false);
        setUploadStep('select');
        setUploadSuccess('');
        // Auto-refresh the page to show new file
        window.location.reload();
      }, 1500);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to upload file';
      setUploadError(errorMessage);
      // Go back to select step on error, but keep the file selected so user can retry
    } finally {
      setUploading(false);
    }
  };

  const handleFolderUpload = async (e) => {
    e.preventDefault();
    
    const inputElement = folderInputRef.current;
    if (!inputElement.files || inputElement.files.length === 0) {
      setUploadError('Please select a folder');
      return;
    }
    
    try {
      setUploadError('');
      setUploadSuccess('');
      setUploading(true);
      setUploadProgress(0);
      setUploadedBytes(0);
      setUploadSpeed(0);
      setUploadTimeRemaining(null);
      uploadStartTimeRef.current = Date.now();

      const files = Array.from(inputElement.files);
      const formData = new FormData();
      
      // Determine the base folder from the first file's webkitRelativePath
      let baseFolderPath = '';
      if (files[0].webkitRelativePath) {
        const pathParts = files[0].webkitRelativePath.split('/');
        baseFolderPath = pathParts[0]; // Get the first directory name
      }
      
      // Append each file and its relative path
      const filePaths = [];
      files.forEach(file => {
        formData.append('files', file);
        const relativePath = file.webkitRelativePath || file.name;
        filePaths.push(relativePath);
      });
      
      // Add the full folder path (current path + base folder)
      let uploadFolderPath = currentPath;
      if (uploadFolderPath && !uploadFolderPath.endsWith('/')) {
        uploadFolderPath += '/';
      }
      formData.append('folder_path', uploadFolderPath);
      
      // Add all file paths
      filePaths.forEach(path => {
        formData.append('file_paths', path);
      });

      const totalSize = files.reduce((sum, f) => sum + f.size, 0);
      setTotalBytes(totalSize);

      // Upload the folder
      const response = await axios.post(
        `${API_URL}/upload-folder`,
        formData,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          },
          params: {
            user_id: user?.id
          },
          onUploadProgress: trackUploadProgress
        }
      );
      
      setUploadSuccess(`Folder uploaded successfully: ${response.data.uploaded.length} files`);
      // Reset the file input
      folderInputRef.current.value = '';
      // Refresh the file list
      fetchFiles(currentPath);
      // Close the modal and refresh page after a delay
      setTimeout(() => {
        setShowFolderUploadModal(false);
        setUploadSuccess('');
        // Auto-refresh the page to show uploaded files
        window.location.reload();
      }, 1500);
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to upload folder';
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };
  
  return (
    <Container>
      <Card className="file-browser">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h4>Files</h4>
              <FileBreadcrumb currentPath={currentPath} />
            </Col>
            <Col xs="auto">
              <Button
                variant="primary"
                className="me-2"
                onClick={() => setShowUploadModal(true)}
              >
                <FaUpload /> Upload File
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowFolderUploadModal(true)}
              >
                <FaFolderPlus /> Upload Folder
              </Button>
              {activeUploads.length > 0 && (
                <Button
                  variant="info"
                  className="ms-2"
                  onClick={() => setShowUploadsManager(true)}
                >
                  Uploads ({activeUploads.length})
                </Button>
              )}
            </Col>
          </Row>
        </Card.Header>
        
        <Card.Body>
          {error && (
            <Alert variant="danger" className="mb-3">{error}</Alert>
          )}
          
          {loading ? (
            <div className="text-center p-5">
              <span>Loading...</span>
            </div>
          ) : (
            <Row>
              <Col>
                <FileList
                  folders={folders}
                  files={files}
                  onFolderClick={handleFolderClick}
                  onDownloadClick={handleDownloadClick}
                  onShareClick={handleShareClick}
                  onDeleteClick={handleDeleteClick}
                  isAdmin={user?.role === 'admin'}
                />
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      
      {/* Share Modal */}
      <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        filePath={selectedFile}
      />
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this file?
          {fileToDelete && <p className="fw-bold">{fileToDelete.split('/').pop()}</p>}
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteFile}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* File Upload Modal */}
      <Modal show={showUploadModal} onHide={() => {
        if (!uploading) {
          setShowUploadModal(false);
          setUploadStep('select');
          setSelectedUploadFile(null);
          setUploadError('');
          setUploadSuccess('');
          setUploadProgress(0);
          setUploadedBytes(0);
          setUploadSpeed(0);
          setUploadTimeRemaining(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Upload File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadStep !== 'select' && !uploading && (
            uploadError ? (
              <Alert variant="danger" dismissible onClose={() => setUploadError('')}>{uploadError}</Alert>
            ) : uploadSuccess ? (
              <Alert variant="success" dismissible onClose={() => setUploadSuccess('')}>{uploadSuccess}</Alert>
            ) : null
          )}
          
          {uploadStep === 'select' ? (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Select File</Form.Label>
                <Form.Control
                  type="file"
                  ref={fileInputRef}
                  disabled={uploading}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      setSelectedUploadFile(file);
                      setUploadError('');
                      setUploadSuccess('');
                      
                      // Automatically determine if multipart upload should be used
                      // 5MB threshold for multipart upload to avoid 413 errors
                      const sizeThreshold = 5 * 1024 * 1024; // 5MB in bytes
                      setUseMultipart(file.size > sizeThreshold);
                    } else {
                      setSelectedUploadFile(null);
                    }
                  }}
                  required
                />
              </Form.Group>
              
              <div className="d-flex justify-content-end">
                <Button 
                  variant="secondary" 
                  className="me-2" 
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadStep('select');
                    setSelectedUploadFile(null);
                    setUploadError('');
                    setUploadSuccess('');
                    setUploadProgress(0);
                    setUploadedBytes(0);
                    setUploadSpeed(0);
                    setUploadTimeRemaining(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  disabled={!selectedUploadFile || uploading}
                  onClick={() => {
                    setUploadError('');
                    setUploadSuccess('');
                    if (useMultipart) {
                      // Show multipart upload component, which starts uploading
                      // immediately (autoStart) as soon as it mounts
                      setUploadStep('multipart');
                    } else {
                      // Start the standard upload immediately instead of
                      // requiring a second "Upload" click
                      setUploadStep('standard');
                      handleFileUpload(new Event('submit'));
                    }
                  }}
                >
                  Continue
                </Button>
              </div>
            </Form>
          ) : uploadStep === 'multipart' ? (
            <>
              <ResumableUpload 
                file={selectedUploadFile}
                folder={currentPath}
                onComplete={(result) => {
                  // Add to active uploads list
                  const upload = {
                    id: Date.now().toString(),
                    file: selectedUploadFile,
                    path: result.filePath,
                    progress: 100,
                    status: 'completed',
                    startTime: Date.now(),
                    endTime: Date.now()
                  };
                  
                  setActiveUploads(prev => [...prev, upload]);
                  
                  // Close modal after successful upload
                  setTimeout(() => {
                    setShowUploadModal(false);
                    setUploadStep('select');
                    setSelectedUploadFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                    // Refresh file list
                    fetchFiles(currentPath);
                  }, 1500);
                }}
                onError={() => {}}
                onProgress={(progress) => {
                  // Update active uploads list
                  const upload = {
                    id: Date.now().toString(),
                    file: selectedUploadFile,
                    path: currentPath + selectedUploadFile.name,
                    progress: progress,
                    status: 'uploading',
                    startTime: Date.now()
                  };
                  
                  // Only add to list if not already present
                  setActiveUploads(prev => {
                    const existing = prev.find(u => 
                      u.file.name === selectedUploadFile.name && 
                      u.path === (currentPath + selectedUploadFile.name)
                    );
                    
                    if (!existing) {
                      return [...prev, upload];
                    }
                    
                    // Update existing upload
                    return prev.map(u => 
                      u.file.name === selectedUploadFile.name && 
                      u.path === (currentPath + selectedUploadFile.name) 
                        ? { ...u, progress, status: progress === 100 ? 'completed' : 'uploading' } 
                        : u
                    );
                  });
                }}
              />
              
              <div className="d-flex justify-content-end mt-3">
                <Button 
                  variant="secondary" 
                  onClick={() => {
                    setUploadStep('select');
                    setSelectedUploadFile(null);
                    setUploadError('');
                    setUploadSuccess('');
                    setUploadProgress(0);
                    setUploadedBytes(0);
                    setUploadSpeed(0);
                    setUploadTimeRemaining(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : uploadStep === 'standard' ? (
            <>
              {uploading ? (
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="fw-semibold">{selectedUploadFile?.name}</span>
                    <span className="text-muted">{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</span>
                  </div>
                  <ProgressBar
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    animated
                  />
                  <div className="d-flex justify-content-between mt-2 text-muted small">
                    <span>{formatBytes(uploadSpeed)}/s</span>
                    <span>{uploadProgress >= 100 ? 'Finishing up...' : formatTimeRemaining(uploadTimeRemaining)}</span>
                  </div>
                </div>
              ) : !uploadSuccess ? (
                // Only reached if the upload hasn't started yet (rare race)
                // or it failed and the user needs to back out or retry
                <div className="d-flex justify-content-end">
                  <Button
                    variant="secondary"
                    className="me-2"
                    onClick={() => {
                      setUploadStep('select');
                      setSelectedUploadFile(null);
                      setUploadError('');
                      setUploadSuccess('');
                      setUploadProgress(0);
                      setUploadedBytes(0);
                      setUploadSpeed(0);
                      setUploadTimeRemaining(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleFileUpload(new Event('submit'))}
                  >
                    {uploadError ? 'Retry' : 'Upload'}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
      </Modal>
      
      
      {/* Folder Upload Modal */}
      <Modal show={showFolderUploadModal} onHide={() => !uploading && (setShowFolderUploadModal(false), setUploadError(''), setUploadSuccess(''), setUploadProgress(0))}>
        <Modal.Header closeButton>
          <Modal.Title>Upload Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!uploading && (
            uploadError ? (
              <Alert variant="danger" dismissible onClose={() => setUploadError('')}>{uploadError}</Alert>
            ) : uploadSuccess ? (
              <Alert variant="success" dismissible onClose={() => setUploadSuccess('')}>{uploadSuccess}</Alert>
            ) : null
          )}
          
          <Form onSubmit={handleFolderUpload}>
            <Form.Group className="mb-3">
              <Form.Label>Select Folder</Form.Label>
              <Form.Control
                type="file"
                ref={folderInputRef}
                webkitdirectory=""
                directory=""
                multiple
                disabled={uploading}
                onChange={() => {
                  setUploadError('');
                  setUploadSuccess('');
                }}
                required
              />
              <Form.Text className="text-muted">
                Select a folder to upload. All files will maintain their folder structure.
              </Form.Text>
            </Form.Group>
            
            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted">Uploading folder...</span>
                  <span className="text-muted">{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</span>
                </div>
                <ProgressBar
                  now={uploadProgress}
                  label={`${uploadProgress}%`}
                  animated
                />
                <div className="d-flex justify-content-between mt-2 text-muted small">
                  <span>{formatBytes(uploadSpeed)}/s</span>
                  <span>{uploadProgress >= 100 ? 'Finishing up...' : formatTimeRemaining(uploadTimeRemaining)}</span>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end">
              <Button
                variant="secondary"
                className="me-2"
                onClick={() => {
                  setShowFolderUploadModal(false);
                  setUploadError('');
                  setUploadSuccess('');
                  setUploadProgress(0);
                  setUploadedBytes(0);
                  setUploadSpeed(0);
                  setUploadTimeRemaining(null);
                }}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Folder'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Uploads Manager Modal */}
      <Modal 
        show={showUploadsManager} 
        onHide={() => setShowUploadsManager(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Uploads Manager</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeUploads.length === 0 ? (
            <Alert variant="info">No active uploads</Alert>
          ) : (
            <div className="uploads-list">
              {activeUploads.map(upload => (
                <Card key={upload.id} className="mb-3">
                  <Card.Body>
                    <Card.Title>{upload.file.name}</Card.Title>
                    <ProgressBar 
                      now={upload.progress} 
                      label={`${upload.progress}%`}
                      variant={upload.status === 'completed' ? 'success' : 'primary'}
                      className="mb-2"
                    />
                    <Row>
                      <Col>
                        <Badge 
                          bg={upload.status === 'completed' ? 'success' : 
                              upload.status === 'paused' ? 'warning' : 
                              upload.status === 'error' ? 'danger' : 'primary'}
                        >
                          {upload.status.charAt(0).toUpperCase() + upload.status.slice(1)}
                        </Badge>
                      </Col>
                      <Col className="text-end">
                        {upload.status === 'completed' && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => {
                              setActiveUploads(prev => 
                                prev.filter(u => u.id !== upload.id)
                              );
                            }}
                          >
                            <FaTrash /> Remove
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowUploadsManager(false)}
          >
            Close
          </Button>
          {activeUploads.length > 0 && (
            <Button 
              variant="danger" 
              onClick={() => {
                // Only clear completed uploads
                setActiveUploads(prev => 
                  prev.filter(u => u.status !== 'completed')
                );
              }}
            >
              Clear Completed
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FileBrowser;
